/**
 * Version comparison utilities for forced-refresh-by-minVersion flow.
 *
 * App versions follow the pattern: "<semver>+<buildId>"
 *   - semver  e.g. "2.2.0"
 *   - buildId e.g. "1780801685013" (epoch ms, numeric string)
 *
 * We compare by (semver, buildId). A running bundle is "below min" when:
 *   - its semver is lower than min's semver, OR
 *   - semvers are equal and buildId is lower than min's buildId.
 *
 * The flow degrades safely: any unparseable input returns false (no force).
 */

export type ParsedVersion = {
  semver: [number, number, number];
  buildId: number;
  raw: string;
};

export function parseAppVersion(raw: string | null | undefined): ParsedVersion | null {
  if (!raw || typeof raw !== "string") return null;
  if (raw === "dev") return null;
  const [semverPart, buildPart] = raw.split("+");
  if (!semverPart) return null;
  const parts = semverPart.split(".").map((p) => Number.parseInt(p, 10));
  if (parts.length < 1 || parts.some((n) => !Number.isFinite(n))) return null;
  const [maj = 0, min = 0, patch = 0] = parts;
  const buildId = buildPart ? Number.parseInt(buildPart, 10) : 0;
  return {
    semver: [maj, min, patch],
    buildId: Number.isFinite(buildId) ? buildId : 0,
    raw,
  };
}

function compareSemver(a: [number, number, number], b: [number, number, number]): number {
  for (let i = 0; i < 3; i++) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return 1;
  }
  return 0;
}

/** Returns true iff running < min (strict). False on any parse failure. */
export function isBelowMinVersion(running: string | null | undefined, min: string | null | undefined): boolean {
  const r = parseAppVersion(running);
  const m = parseAppVersion(min);
  if (!r || !m) return false;
  const cmp = compareSemver(r.semver, m.semver);
  if (cmp !== 0) return cmp < 0;
  return r.buildId < m.buildId;
}
